import { onEvent, removeEvent, addClass } from "@kluntje/js-utils/lib/dom-helpers";
import { INITIALIZED } from "Constants/cssClasses";
import { CHECKED_EVENT } from "Constants/eventTypes";
import { FormField } from "Components/abstract/sde-e-form-field/sde-e-form-field";
import { undefinedOnEmpty } from "Helper/stringHelper/undefinedOnEmpty";

export class CheckableFormField extends FormField<boolean> {
  private initialCheckedState = false;
  private initialContentCleared = false;
  public labelMarkup?: string;

  protected get DEFAULT_ERROR_MESSAGE(): string {
    return "Please select this Field.";
  }

  public get isChecked(): boolean {
    return this.hasAttribute("checked") && this.getAttribute("checked") !== "false";
  }

  public set isChecked(isChecked: boolean) {
    this.ui.input.checked = isChecked;
    this.setAttribute("checked", `${isChecked}`);
  }

  protected get submitValueType(): string {
    return "boolean";
  }

  public get submitValue(): boolean {
    return this.isChecked;
  }

  public get submitString(): string | undefined {
    return this.isChecked ? "true" : "false";
  }

  static get observedAttributes(): string[] {
    return ["checked", "disabled"];
  }

  protected connectedCallback(): void {
    super.connectedCallback();
    this.initLabelMarkup();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    const wasChecked = (oldValue === "true" || oldValue === "") && oldValue !== null;
    const isChecked = (newValue === "true" || newValue === "") && newValue !== null;
    if (wasChecked === isChecked || !this.state.initialized) return;

    switch (name) {
      case "checked":
        if (this.ui.input.checked !== isChecked) {
          this.ui.input.checked = isChecked;
          this.handleCheckedChange();
        }
        break;
      case "disabled":
        this.ui.input.disabled = isChecked;
        break;
    }
  }

  private clearInitialContent(): void {
    if (this.initialContentCleared === false) {
      this.innerHTML = "";
      this.initialContentCleared = true;
    }
  }

  private initLabelMarkup() {
    if (this.initialContentCleared === true) return;
    this.labelMarkup = undefinedOnEmpty(this.innerHTML.trim());
    this.clearInitialContent();
  }

  public async isValid(): Promise<boolean> {
    await Promise.resolve();
    return this.ui.input.checkValidity() || this.isChecked;
  }

  protected async initFormField() {
    this.initialCheckedState = this.isChecked;
    this.ui.input.checked = this.isChecked;
    await this.prefillInput();
    addClass(this, INITIALIZED);
  }

  protected async prefillInput() {
    await Promise.resolve();
    const prefillValue = this.prefillValue;
    if (prefillValue === null) return;
    this.isChecked = prefillValue !== "false";
  }

  public async handleCheckedChange(): Promise<void> {
    const isChecked = this.ui.input.checked;
    this.isChecked = isChecked;
    await this.renderAsync();
    this.dispatchEvent(
      new CustomEvent(CHECKED_EVENT, {
        detail: {
          isChecked,
          target: this,
          name: this.name,
        },
      }),
    );
  }

  protected async onValidationModeChange({ inValidationMode }: any) {
    await Promise.resolve();
    if (inValidationMode) {
      onEvent(this, CHECKED_EVENT, this.validate, this);
    } else {
      removeEvent(this, CHECKED_EVENT, this.validate, this);
    }
  }

  public async reset(): Promise<void> {
    this.setState({ showError: false, inValidationMode: false });
    this.isChecked = this.initialCheckedState;
    await this.prefillInput();
  }
}
